import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Camera, Scissors, Monitor, SlidersHorizontal, ChevronDown, ChevronRight, Film, Palette, Music, Plus, Laptop, Landmark, FolderOpen, X, Play, Pause, Loader2, Wand2, Rocket } from "lucide-react";
import { editorApi, streamAsset } from "@/api/mist";

const FILTER_OPTIONS = [
  { key: "comic", label: "Comic" },
  { key: "cartoon", label: "Cartoon" },
  { key: "anime", label: "AnimeGANv2 ONNX (Heavy CPU)" },
];
const ALIX_AVATAR = "/alix-avatar.png";
const LAYOUT_OPTIONS = ["🖼️ 3-Panel Comic Strip", "🎨 4-Box Grid Template"];

const fmt = (t) => {
  if (!t || Number.isNaN(t)) return "00:00";
  const s = Math.floor(t % 60), m = Math.floor(t / 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const Toggle = ({ testid, label, note, on, onToggle }) => (
  <button type="button" data-testid={testid} onClick={onToggle}
    className="w-full flex items-start gap-3 border border-white/15 bg-black/30 px-3 py-2.5 text-left hover:border-gold/40 transition-colors">
    <span className={`mt-0.5 shrink-0 w-9 h-5 border-2 flex items-center px-0.5 transition-colors ${on ? "border-gold bg-gold/20 justify-end" : "border-white/30 justify-start"}`}>
      <span className={`w-3 h-3 ${on ? "bg-gold" : "bg-white/40"}`} />
    </span>
    <span className="min-w-0">
      <span className={`block font-mono text-[11px] font-black uppercase tracking-wider ${on ? "text-gold" : "text-white/70"}`}>[ {on ? "●" : "○"} ] {label}</span>
      <span className="block font-mono text-[9px] text-white/35 mt-0.5">{note}</span>
    </span>
  </button>
);

const SourceRow = ({ testid, icon: Icon, title, desc, onClick }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className="w-full flex items-center gap-3 border-2 border-white/20 bg-black/40 px-4 py-3 text-left hover:border-gold/60 hover:bg-gold/5 transition-colors group">
    <span className="w-9 h-9 shrink-0 grid place-items-center border-2 border-gold/40 bg-black/50 group-hover:border-gold transition-colors">
      <Icon size={16} strokeWidth={2.4} className="text-gold" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-mono text-xs font-black uppercase tracking-wider text-white/90">{title}</span>
      <span className="block font-mono text-[9px] text-white/40 mt-0.5">{desc}</span>
    </span>
    <ChevronRight size={16} strokeWidth={2.4} className="text-white/30 group-hover:text-gold transition-colors" />
  </button>
);

export default function VideoEditorWorkspace() {
  const [srcOpen, setSrcOpen] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [vaultVideos, setVaultVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [inPct, setInPct] = useState(0);
  const [outPct, setOutPct] = useState(100);
  const [style, setStyle] = useState("cartoon");
  const [lock30, setLock30] = useState(false);
  const [smooth, setSmooth] = useState(false);
  const [bubbles, setBubbles] = useState(false);
  const [busy, setBusy] = useState(null);      // "ingest" | "trim" | "frame"
  const [job, setJob] = useState(null);         // stylize job {progress,stage,frame,total}
  const [musicOpen, setMusicOpen] = useState(false);
  const [vaultAudio, setVaultAudio] = useState([]);
  const [music, setMusic] = useState(null);     // {asset_id, filename}
  const [musicLoading, setMusicLoading] = useState(false);
  const [musicOffset, setMusicOffset] = useState(0);   // seconds — start delay on the music bed
  const [fadeIn, setFadeIn] = useState(0);
  const [fadeOut, setFadeOut] = useState(0);
  const [fixBarrel, setFixBarrel] = useState(false);
  const [sharpen, setSharpen] = useState(false);
  const [denoise, setDenoise] = useState(false);
  const [lowCut, setLowCut] = useState(false);
  const [enhanceVoice, setEnhanceVoice] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [captioning, setCaptioning] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);   // finished stylized asset_id

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const scrubRef = useRef(null);
  const musicLaneRef = useRef(null);
  const urlRef = useRef(null);
  const aliveRef = useRef(true);
  const jobStartRef = useRef(0);

  useEffect(() => () => { aliveRef.current = false; if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const loadAsset = useCallback(async (id) => {
    try {
      const url = await streamAsset(id);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setVideoUrl(url); setAssetId(id);
      setSrcOpen(false); setVaultOpen(false);
      setInPct(0); setOutPct(100); setCurrent(0); setPlaying(false);
    } catch { toast.error("Couldn't load that video"); }
  }, []);

  const onDevice = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("ingest");
    try {
      const res = await editorApi.ingestDevice(f);
      setResult(null);
      await loadAsset(res.asset_id);
      toast.success("Video loaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed — try a different video file"); }
    finally { setBusy(null); }
  };

  const openVault = async () => {
    setVaultOpen(true);
    try { const r = await editorApi.videos(); setVaultVideos(r.videos || []); }
    catch { setVaultVideos([]); }
  };

  const openMusic = async () => {
    setMusicOpen(true); setMusicLoading(true);
    try { const r = await editorApi.audio(); setVaultAudio(r.audio || []); }
    catch { setVaultAudio([]); }
    finally { setMusicLoading(false); }
  };

  const publishPost = async () => {
    if (!result) return;
    setPublishing(true);
    try {
      await editorApi.postToFeed(result, caption.trim(), visibility);
      toast.success("Posted to feed · Generated via ALIX Anime Studio");
      setPostOpen(false); setCaption("");
    } catch { toast.error("Couldn't post to feed"); }
    finally { setPublishing(false); }
  };

  const generateTags = async () => {
    setCaptioning(true);
    try {
      const r = await editorApi.aiCaption(style);
      setCaption(r.caption || "");
      toast.success("ALIX wrote your caption");
    } catch { toast.error("Caption generation failed"); }
    finally { setCaptioning(false); }
  };

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  const onMeta = () => { const v = videoRef.current; if (v) setDuration(v.duration || 0); };
  const onTime = () => { const v = videoRef.current; if (v) setCurrent(v.currentTime || 0); };

  const inSec = (inPct / 100) * duration;
  let outSec = lock30 && duration ? Math.min(duration, inSec + 30) : (outPct / 100) * duration;
  if (style === "anime" && duration) outSec = Math.min(outSec, inSec + 60); // hard 60s cap
  const clipLen = Math.max(0, outSec - inSec);

  const startDrag = (which) => (e) => {
    e.preventDefault();
    const move = (ev) => {
      const rect = scrubRef.current?.getBoundingClientRect(); if (!rect) return;
      const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
      let pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
      if (which === "start") setInPct(Math.min(pct, outPct - 1));
      else setOutPct(Math.max(pct, inPct + 1));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const startMusicDrag = (e) => {
    if (!music || !clipLen) return;
    e.preventDefault();
    const move = (ev) => {
      const rect = musicLaneRef.current?.getBoundingClientRect(); if (!rect) return;
      const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      setMusicOffset(+(pct * clipLen).toFixed(1));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const takeScreenshot = async () => {
    const v = videoRef.current; if (!v) return;
    setBusy("frame");
    try {
      const c = canvasRef.current;
      c.width = v.videoWidth || 1280; c.height = v.videoHeight || 720;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      const blob = await new Promise((res) => c.toBlob(res, "image/png"));
      await editorApi.saveFrame(blob);
      toast.success("Frame saved to your Vault");
    } catch { toast.error("Screenshot failed"); }
    finally { setBusy(null); }
  };

  const doTrim = async () => {
    if (!assetId) return;
    setBusy("trim");
    try {
      const r = await editorApi.trim(assetId, +inSec.toFixed(2), +outSec.toFixed(2));
      toast.success("Trimmed clip saved");
      await loadAsset(r.asset_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Trim failed"); }
    finally { setBusy(null); }
  };

  const runStylize = async () => {
    if (!assetId) { toast.error("Load a video first"); return; }
    if (!duration) { jobStartRef.current = 0; toast.error("Video still loading — try again in a moment"); return; }
    setJob({ progress: 0, stage: "queued" });
    try {
      jobStartRef.current = Date.now();
      const moff = Math.min(Math.max(0, musicOffset), Math.max(0, clipLen - 0.1));
      const { job_id } = await editorApi.stylize(assetId, {
        style,
        start: +inSec.toFixed(2),
        end: +outSec.toFixed(2),
        music_asset_id: music?.asset_id || null,
        smooth,
        fix_barrel: fixBarrel,
        sharpen,
        music_offset: +moff.toFixed(2),
        fade_in: +Math.max(0, fadeIn).toFixed(2),
        fade_out: +Math.max(0, fadeOut).toFixed(2),
        denoise,
        low_cut: lowCut,
        enhance_voice: enhanceVoice,
      });
      const started = Date.now();
      while (Date.now() - started < 600000 && aliveRef.current) {
        await new Promise((r) => setTimeout(r, 2500));
        const j = await editorApi.job(job_id);
        setJob(j);
        if (j.status === "completed") { toast.success("Stylized reel saved"); setResult(j.asset_id); await loadAsset(j.asset_id); break; }
        if (j.status === "failed") { toast.error(j.error || "Stylize failed"); break; }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Stylize failed"); }
    finally { setTimeout(() => setJob(null), 1500); }
  };

  const hasVideo = !!videoUrl;
  const btn = (enabled) => enabled ? "opacity-100 cursor-pointer hover:border-gold" : "opacity-70 cursor-not-allowed";
  const etaLabel = (() => {
    if (!job || !job.frame || !job.total || !jobStartRef.current) return "estimating…";
    const el = (Date.now() - jobStartRef.current) / 1000;
    return fmt(Math.max(0, (el / job.frame) * (job.total - job.frame)));
  })();

  return (
    <div data-testid="video-editor-workspace" className="relative h-full w-full flex flex-col gap-4 sm:gap-5 bg-charcoal text-[#FDFBF7] p-3 sm:p-6 overflow-y-auto font-mono">
      <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={onDevice} data-testid="editor-device-input" />
      <canvas ref={canvasRef} className="hidden" />

      {/* Title bar */}
      <div className="flex items-center justify-between bg-black border-2 border-gold/60 shadow-[6px_6px_0px_0px_rgba(242,201,76,0.35)] px-5 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <Film size={20} strokeWidth={2.6} className="text-gold" />
          <h1 className="font-mono text-sm font-black uppercase tracking-[0.22em]">MiSt · Video Editor</h1>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">{hasVideo ? "clip loaded" : "$0 local engine"}</span>
      </div>

      {/* TOP HALF */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 lg:gap-5 lg:flex-1 lg:min-h-[300px]">
        {/* Monitor */}
        <section data-testid="editor-monitor-box" className="flex flex-col border-2 border-gold/50 bg-black/40 overflow-hidden min-h-[300px] lg:min-h-0">
          <header className="flex items-center gap-2 px-4 py-2.5 border-b-2 border-gold/50 bg-black/60 shrink-0">
            <Monitor size={14} strokeWidth={2.6} className="text-gold" />
            <h2 className="font-mono text-[11px] font-black uppercase tracking-[0.16em] text-gold">🖥️ MONITOR: {hasVideo ? "LIVE_STREAM" : "AWAITING_STREAM"}</h2>
          </header>
          <div className="flex-1 min-h-0 p-4 flex flex-col gap-4">
            {hasVideo ? (
              <video ref={videoRef} src={videoUrl} onLoadedMetadata={onMeta} onTimeUpdate={onTime}
                onEnded={() => setPlaying(false)} playsInline data-testid="editor-video"
                className="flex-1 min-h-[160px] w-full bg-black object-contain border-2 border-white/20" />
            ) : (
              <button type="button" data-testid="editor-monitor-screen" onClick={() => setSrcOpen(true)}
                className="flex-1 min-h-[160px] grid place-items-center border-2 border-dashed border-white/20 bg-[#0b0f16] cursor-pointer hover:border-gold/50 transition-colors group"
                style={{ backgroundImage: "radial-gradient(circle at 50% 40%, rgba(242,201,76,0.06), transparent 60%)" }}>
                <div className="text-center">
                  {busy === "ingest" ? <Loader2 size={30} className="text-gold/70 mx-auto mb-2 animate-spin" /> : <Film size={30} strokeWidth={1.6} className="text-white/25 group-hover:text-gold/60 mx-auto mb-2 transition-colors" />}
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-white/40 group-hover:text-gold/80 transition-colors">{busy === "ingest" ? "Loading…" : "Awaiting Video"}</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/25 mt-2">Click to select source</p>
                </div>
              </button>
            )}
            <div className="grid grid-cols-2 gap-3 shrink-0">
              <button type="button" disabled={!hasVideo || busy} onClick={takeScreenshot} data-testid="editor-screenshot-btn"
                className={`flex items-center justify-center gap-2 border-2 border-white/30 bg-white/10 px-3 py-2.5 font-mono text-[11px] font-black uppercase tracking-wider text-white/85 transition-colors ${btn(hasVideo && !busy)}`}>
                {busy === "frame" ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} strokeWidth={2.4} />} 📸 Take Screenshot
              </button>
              <button type="button" disabled={!hasVideo || busy} onClick={doTrim} data-testid="editor-trim-btn"
                className={`flex items-center justify-center gap-2 border-2 border-white/30 bg-white/10 px-3 py-2.5 font-mono text-[11px] font-black uppercase tracking-wider text-white/85 transition-colors ${btn(hasVideo && !busy)}`}>
                {busy === "trim" ? <Loader2 size={14} className="animate-spin" /> : <Scissors size={14} strokeWidth={2.4} />} ✄ Trim Video
              </button>
            </div>
          </div>
        </section>

        {/* Style config */}
        <section data-testid="editor-style-box" className="flex flex-col border border-gold/40 bg-black/40 overflow-hidden min-h-[300px] lg:min-h-0">
          <header className="flex items-center gap-2 px-4 py-2.5 border-b border-gold/40 bg-black/60 shrink-0">
            <SlidersHorizontal size={14} strokeWidth={2.6} className="text-gold" />
            <h2 className="font-mono text-[11px] font-black uppercase tracking-[0.16em] text-gold">⚙️ STYLE_PIPELINE_CONFIG</h2>
          </header>
          <div className="flex-1 min-h-0 p-4 flex flex-col gap-3 overflow-y-auto">
            <label className="font-mono text-[9px] uppercase tracking-widest text-white/40">Filter Style</label>
            <div className="flex flex-col gap-2">
              {FILTER_OPTIONS.map((opt) => (
                <button key={opt.key} type="button" onClick={() => setStyle(opt.key)} data-testid={`editor-filter-option-${opt.key}`}
                  className={`flex items-center gap-2 border px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${style === opt.key ? "border-gold bg-gold/15 text-gold" : "border-white/10 bg-white/5 text-white/45 hover:border-white/30"}`}>
                  <span className={`w-2 h-2 rounded-full border ${style === opt.key ? "border-gold bg-gold" : "border-gold/50"}`} />
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-1">
              <button type="button" onClick={runStylize} disabled={!hasVideo || !!job} data-testid="editor-run-stylize-btn"
                className={`flex items-center justify-center gap-2 border-2 px-3 py-2.5 font-mono text-[11px] font-black uppercase tracking-wider transition-colors shrink-0 ${hasVideo && !job ? "border-gold bg-gold text-black hover:bg-gold/80" : "border-white/20 bg-white/10 text-white/60 cursor-not-allowed"}`}>
                <Wand2 size={14} strokeWidth={2.6} /> ▶ Run
              </button>
              {job && (
                <div data-testid="editor-progress-chip" className="flex-1 flex items-center gap-2 border-2 border-gold/60 bg-black/70 px-2 py-1 overflow-hidden">
                  <img src={ALIX_AVATAR} alt="ALIX" className="w-8 h-8 rounded-full object-cover object-top border border-gold/70 shrink-0 animate-pulse" />
                  <span className="font-mono text-[9px] leading-tight text-gold min-w-0">
                    <span className="text-white/50">● RUNNING:</span> FRAME {job.frame || 0} / {job.total || "…"}<br />
                    <span className="text-white/40">EST:</span> {etaLabel} · {job.progress || 0}%
                  </span>
                </div>
              )}
            </div>
            {job && (
              <span data-testid="editor-bake-subline" className="font-mono text-[9px] italic text-gold/70">ALIX is painting your frames…</span>
            )}
            {job && job.preview && (
              <img data-testid="editor-bake-thumb" src={job.preview} alt="latest painted frame" className="w-full max-h-28 object-contain border-2 border-gold/40 bg-black/50" />
            )}
            {!job && result && (
              <button type="button" onClick={() => setPostOpen(true)} data-testid="editor-post-feed-btn"
                className="flex items-center justify-center gap-2 border-2 border-gold bg-gold text-black px-3 py-2.5 font-mono text-[11px] font-black uppercase tracking-wider hover:bg-gold/80 transition-colors">
                <Rocket size={14} strokeWidth={2.6} /> 🚀 Post to Mist_Feed
              </button>
            )}
            {style === "anime" && (
              <p data-testid="editor-anime-caps" className="font-mono text-[8px] uppercase tracking-widest text-gold/70">🔒 Heavy CPU (local ONNX) · capped 720p · 60s max</p>
            )}

            <label className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-2">Panel Layout</label>
            <div className="flex items-center justify-between border-2 border-white/25 bg-[#0b0f16] px-3 py-2.5 font-mono text-xs uppercase tracking-wider text-white/80" data-testid="editor-layout-dropdown">
              🎞️ Single Continuous Video <ChevronDown size={15} strokeWidth={2.4} />
            </div>
            <div className="flex flex-col gap-2">
              {LAYOUT_OPTIONS.map((opt) => (
                <div key={opt} data-testid="editor-layout-option" className="flex items-center gap-2 border border-white/10 bg-white/5 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-white/45">
                  <ChevronRight size={12} strokeWidth={2.6} className="text-gold/50" />{opt}
                </div>
              ))}
            </div>

            <label className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-2">Auto Utilities</label>
            <Toggle testid="editor-toggle-smooth" label="Auto_Smooth_Shake" note="(Fixes shaky action-cam / bouncy steps)" on={smooth} onToggle={() => setSmooth((v) => !v)} />
            <Toggle testid="editor-toggle-bubbles" label="Auto_Speech_Bubbles" note="(Auto-transcribes voice audio into comic text bubbles)" on={bubbles} onToggle={() => setBubbles((v) => !v)} />

            <label className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-2">Mini-Cam Fixes</label>
            <Toggle testid="editor-toggle-barrel" label="Fix_Barrel_Warp" note="(Flattens action-cam / GoPro wide-angle bulge)" on={fixBarrel} onToggle={() => setFixBarrel((v) => !v)} />
            <Toggle testid="editor-toggle-sharpen" label="Harden_Pixels" note="(Crisp unsharp mask — sharpens soft footage)" on={sharpen} onToggle={() => setSharpen((v) => !v)} />

            <label className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-2">Audio Cleanup <span className="text-white/25 normal-case tracking-normal">· $0 local FFmpeg</span></label>
            <Toggle testid="editor-toggle-enhance" label="AI_Voice_Enhance" note="(Neural RNNoise + low-cut + leveling — broadcast-clean voice)" on={enhanceVoice} onToggle={() => setEnhanceVoice((v) => !v)} />
            <Toggle testid="editor-toggle-denoise" label="Noise_Reduction" note="(Removes hiss / hum / broadband background noise)" on={denoise} onToggle={() => setDenoise((v) => !v)} />
            <Toggle testid="editor-toggle-lowcut" label="Low_Cut_100Hz" note="(Kills rumble, wind & mic-handling below 100Hz)" on={lowCut} onToggle={() => setLowCut((v) => !v)} />
          </div>
        </section>
      </div>

      {/* BOTTOM HALF — Timeline */}
      <section data-testid="editor-timeline-stitcher" className="flex flex-col border-4 border-black shadow-[8px_8px_0px_0px_#000] bg-black/50 overflow-hidden flex-1 min-h-[240px]">
        <header className="flex items-center gap-2 px-4 py-3 border-b-4 border-black bg-[#F2C94C] shrink-0">
          <Film size={16} strokeWidth={2.6} className="text-black" />
          <h2 className="font-mono text-xs font-black uppercase tracking-[0.18em] text-black">🎞️ TIMELINE_STITCHER_V1.0</h2>
        </header>

        <div data-testid="editor-timeline-controls" className="border-b-4 border-black bg-black/60 px-4 py-3 flex flex-col gap-3 shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={togglePlay} disabled={!hasVideo} data-testid="editor-play-btn"
              className={`inline-flex items-center gap-1.5 border-2 border-gold/60 bg-gold/15 px-3 py-1.5 font-mono text-[11px] font-black uppercase tracking-wider text-gold ${hasVideo ? "hover:bg-gold/25" : "opacity-60 cursor-not-allowed"}`}>
              {playing ? <><Pause size={13} strokeWidth={2.8} /> ⏸ Pause</> : <><Play size={13} strokeWidth={2.8} /> ▶ Play</>}
            </button>
            <span data-testid="editor-timecode" className="font-mono text-[11px] font-black tracking-wider text-white/70 border border-white/20 bg-black/50 px-3 py-1.5">
              TIME: <span className="text-gold">{fmt(current)}</span> / {fmt(duration)}
            </span>
            <div className="ml-auto w-72 max-w-full">
              <Toggle testid="editor-toggle-30s" label="Lock_30s_Reel_Window" note="(Locks a fixed 30s block — drag it across the clip)" on={lock30} onToggle={() => setLock30((v) => !v)} />
            </div>
          </div>

          <div className="pt-1">
            <div ref={scrubRef} data-testid="editor-scrubber" className="relative h-9 border-2 border-white/20 bg-[#0b0f16] select-none">
              <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 40px)" }} />
              {duration > 0 && <div className="absolute top-0 bottom-0 w-0.5 bg-white/70" style={{ left: `${(current / duration) * 100}%` }} data-testid="editor-playhead" />}
              <div data-testid="editor-trim-range" className="absolute top-0 bottom-0 bg-gold/25 border-x-2 border-gold" style={{ left: `${inPct}%`, right: `${100 - (lock30 && duration ? Math.min(100, inPct + (30 / duration) * 100) : outPct)}%` }} />
              <div className="absolute top-0 bottom-0 z-10" style={{ left: `${inPct}%` }}>
                <span data-testid="editor-handle-start" onPointerDown={startDrag("start")} className="absolute top-0 bottom-0 -translate-x-1/2 w-6 grid place-items-center bg-gold text-black border-2 border-black font-mono text-[11px] font-black leading-none cursor-ew-resize touch-none">[|</span>
              </div>
              {!lock30 && (
                <div className="absolute top-0 bottom-0 z-10" style={{ left: `${outPct}%` }}>
                  <span data-testid="editor-handle-end" onPointerDown={startDrag("end")} className="absolute top-0 bottom-0 -translate-x-1/2 w-6 grid place-items-center bg-gold text-black border-2 border-black font-mono text-[11px] font-black leading-none cursor-ew-resize touch-none">|]</span>
                </div>
              )}
            </div>
            <div className="flex justify-between mt-1 font-mono text-[8px] uppercase tracking-widest text-white/40">
              <span>IN {fmt(inSec)}</span>
              <span className="text-gold/70">RANGE {fmt(Math.max(0, outSec - inSec))}</span>
              <span>OUT {fmt(outSec)}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-4 relative">
          <div className="flex flex-col gap-3">
            <div data-testid="editor-track-video" className="flex items-stretch border-2 border-blue-500/60 bg-black/30 min-h-[56px]">
              <div className="w-24 sm:w-48 shrink-0 flex items-center gap-2 px-2 sm:px-4 border-r-2 border-blue-500/60 bg-blue-500/10"><Film size={15} className="text-blue-300" /><span className="font-mono text-[10px] sm:text-[11px] font-black uppercase tracking-[0.14em] text-blue-300">🎞️ Video Track</span></div>
              <div className="flex-1 relative">{hasVideo && <div className="absolute inset-y-2 left-2 right-2 bg-blue-500/25 border border-blue-400/50 grid place-items-center font-mono text-[9px] uppercase tracking-wider text-blue-100">clip · {fmt(duration)}</div>}</div>
            </div>
            <div data-testid="editor-track-filters" className="flex items-stretch border-2 border-orange-500/60 bg-black/30 min-h-[56px]">
              <div className="w-24 sm:w-48 shrink-0 flex items-center gap-2 px-2 sm:px-4 border-r-2 border-orange-500/60 bg-orange-500/10"><Palette size={15} className="text-orange-300" /><span className="font-mono text-[10px] sm:text-[11px] font-black uppercase tracking-[0.14em] text-orange-300">🎨 Cartoon Filters</span></div>
              <div className="flex-1 relative">{hasVideo && <div className="absolute inset-y-2 left-2 w-40 bg-orange-500/25 border border-orange-400/50 grid place-items-center font-mono text-[9px] uppercase tracking-wider text-orange-100">{style}</div>}</div>
            </div>
            <div data-testid="editor-track-music" className="flex items-stretch border-2 border-purple-500/60 bg-black/30 min-h-[56px]">
              <div className="w-40 sm:w-56 shrink-0 flex flex-col justify-center gap-1 px-2 sm:px-4 border-r-2 border-purple-500/60 bg-purple-500/10">
                <div className="flex items-center gap-2"><Music size={15} className="text-purple-300" /><span className="font-mono text-[11px] font-black uppercase tracking-[0.14em] text-purple-300">🎵 Music Track</span></div>
                <button type="button" onClick={openMusic} data-testid="editor-browse-music-btn" className="mt-0.5 inline-flex items-center gap-1 border border-purple-400/50 bg-purple-500/15 px-2 py-1 font-mono text-[9px] font-black uppercase tracking-wider text-purple-200 hover:border-purple-300 transition-colors self-start"><Plus size={11} strokeWidth={3} /> Browse Safe Music</button>
                {music && (
                  <>
                    <label className="flex items-center gap-1.5 mt-1 font-mono text-[9px] font-black uppercase tracking-wider text-purple-200">
                      START_AT
                      <input type="number" min="0" max={Math.max(0, clipLen)} step="0.1" value={musicOffset} data-testid="editor-music-offset-input"
                        onChange={(e) => setMusicOffset(Math.max(0, Math.min(clipLen || 0, parseFloat(e.target.value) || 0)))}
                        className="w-14 bg-black border border-purple-400/60 px-1.5 py-0.5 text-purple-100 text-[10px] font-black text-center focus:outline-none focus:border-purple-300" />s
                    </label>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                      <label className="flex items-center gap-1 font-mono text-[8px] font-black uppercase tracking-wider text-white/50">
                        FADE↑
                        <input type="number" min="0" max="10" step="0.5" value={fadeIn} data-testid="editor-fade-in-input"
                          onChange={(e) => setFadeIn(Math.max(0, parseFloat(e.target.value) || 0))}
                          className="w-10 bg-black border border-white/25 px-1 py-0.5 text-white/80 text-[9px] font-black text-center focus:outline-none focus:border-purple-300" />
                      </label>
                      <label className="flex items-center gap-1 font-mono text-[8px] font-black uppercase tracking-wider text-white/50">
                        FADE↓
                        <input type="number" min="0" max="10" step="0.5" value={fadeOut} data-testid="editor-fade-out-input"
                          onChange={(e) => setFadeOut(Math.max(0, parseFloat(e.target.value) || 0))}
                          className="w-10 bg-black border border-white/25 px-1 py-0.5 text-white/80 text-[9px] font-black text-center focus:outline-none focus:border-purple-300" />
                      </label>
                    </div>
                  </>
                )}
              </div>
              <div ref={musicLaneRef} className="flex-1 relative select-none">
                {music && (
                  <div data-testid="editor-music-clip" onPointerDown={startMusicDrag}
                    className="absolute inset-y-2 bg-purple-500/30 border-2 border-purple-400/70 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-purple-100 truncate px-2 cursor-ew-resize touch-none hover:bg-purple-500/40 transition-colors"
                    style={{ left: `calc(${clipLen ? Math.min(95, (musicOffset / clipLen) * 100) : 0}% + 8px)`, right: "8px" }}>
                    <SlidersHorizontal size={11} className="text-purple-300 shrink-0" />♪ {music.filename}
                  </div>
                )}
              </div>
            </div>
          </div>
          {!hasVideo && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none px-4">
              <button type="button" onClick={() => setSrcOpen(true)} data-testid="editor-dropzone-hint" className="pointer-events-auto border-2 border-dashed border-gold/50 bg-black/70 px-6 py-4 text-center backdrop-blur-[1px] hover:border-gold transition-colors">
                <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.18em] text-gold/90">Drag and drop your MP4 video file here to start.</p>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* SOURCE MODAL */}
      {srcOpen && (
        <div data-testid="editor-source-modal" className="absolute inset-0 z-30 grid place-items-center bg-black/75 p-6">
          <div className="w-full max-w-md border-2 border-gold/60 bg-charcoal shadow-[8px_8px_0px_0px_rgba(0,0,0,0.6)]">
            <header className="flex items-center gap-2 px-4 py-3 border-b border-gold/50 bg-black/60">
              <FolderOpen size={15} strokeWidth={2.6} className="text-gold" />
              <h3 className="font-mono text-[11px] font-black uppercase tracking-[0.16em] text-gold">📂 Select Video Source / Project</h3>
            </header>
            <div className="p-4 flex flex-col gap-3">
              {vaultOpen ? (
                <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                  {vaultVideos.length === 0 ? (
                    <p className="font-mono text-[10px] text-white/40 py-6 text-center">No saved videos yet.</p>
                  ) : vaultVideos.map((v) => (
                    <button key={v.asset_id} data-testid="editor-vault-video" onClick={() => { setResult(null); loadAsset(v.asset_id); }}
                      className="w-full flex items-center gap-3 border-2 border-white/20 bg-black/40 px-4 py-2.5 text-left hover:border-gold/60 hover:bg-gold/5 transition-colors">
                      <Film size={14} className="text-gold shrink-0" />
                      <span className="font-mono text-[10px] text-white/80 truncate">{v.filename}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <SourceRow testid="editor-source-device" icon={Laptop} title="💻 From Device" desc="Upload a new MP4 video file from local storage." onClick={() => fileRef.current?.click()} />
                  <SourceRow testid="editor-source-vault" icon={Landmark} title="🏛️ From Vault" desc="Choose an existing video asset from your secure library." onClick={openVault} />
                  <SourceRow testid="editor-source-recent" icon={FolderOpen} title="📂 Recent Projects" desc="Open a saved draft timeline to resume work." onClick={() => toast("Saved projects are coming soon", { icon: "📂" })} />
                </>
              )}
              <div className="flex justify-end pt-1">
                <button type="button" data-testid="editor-source-cancel" onClick={() => { setSrcOpen(false); setVaultOpen(false); }}
                  className="inline-flex items-center gap-1 font-mono text-[10px] font-black uppercase tracking-widest text-white/50 hover:text-gold transition-colors">
                  <X size={12} strokeWidth={3} /> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ONSITE SAFE MUSIC MODAL */}
      {musicOpen && (
        <div data-testid="editor-music-modal" className="absolute inset-0 z-30 grid place-items-center bg-black/75 p-6">
          <div className="w-full max-w-md border-2 border-purple-400/60 bg-charcoal shadow-[8px_8px_0px_0px_rgba(0,0,0,0.6)]">
            <header className="flex items-center gap-2 px-4 py-3 border-b border-purple-400/50 bg-black/60">
              <Music size={15} strokeWidth={2.6} className="text-purple-300" />
              <h3 className="font-mono text-[11px] font-black uppercase tracking-[0.16em] text-purple-200">🎵 Onsite Safe Music</h3>
            </header>
            <div className="p-4 flex flex-col gap-2 max-h-80 overflow-y-auto">
              {musicLoading ? (
                <p className="font-mono text-[10px] text-white/40 py-6 text-center">Loading onsite music…</p>
              ) : vaultAudio.length === 0 ? (
                <p className="font-mono text-[10px] text-white/40 py-6 text-center">No onsite music in your vault yet.</p>
              ) : vaultAudio.map((a) => (
                <button key={a.asset_id} data-testid="editor-music-track" onClick={() => { setMusic(a); setMusicOpen(false); toast.success("Music attached to timeline"); }}
                  className="w-full flex items-center gap-3 border-2 border-white/20 bg-black/40 px-4 py-2.5 text-left hover:border-purple-400 hover:bg-purple-500/10 transition-colors">
                  <Music size={14} className="text-purple-300 shrink-0" />
                  <span className="font-mono text-[10px] text-white/80 truncate">{a.filename}</span>
                </button>
              ))}
              <div className="flex justify-end pt-1">
                <button type="button" onClick={() => setMusicOpen(false)} className="inline-flex items-center gap-1 font-mono text-[10px] font-black uppercase tracking-widest text-white/50 hover:text-purple-300 transition-colors"><X size={12} strokeWidth={3} /> Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POST TO FEED MODAL */}
      {postOpen && (
        <div data-testid="editor-post-modal" className="absolute inset-0 z-40 grid place-items-center bg-black/80 p-6">
          <div className="w-full max-w-md border-2 border-gold/60 bg-charcoal shadow-[8px_8px_0px_0px_rgba(0,0,0,0.6)]">
            <header className="flex items-center gap-2 px-4 py-3 border-b border-gold/50 bg-black/60">
              <Rocket size={15} strokeWidth={2.6} className="text-gold" />
              <h3 className="font-mono text-[11px] font-black uppercase tracking-[0.16em] text-gold">🚀 Publish to Mist_Feed</h3>
            </header>
            <div className="p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <label className="font-mono text-[9px] uppercase tracking-widest text-white/40">Caption</label>
                <button type="button" onClick={generateTags} disabled={captioning} data-testid="editor-generate-tags-btn"
                  className={`inline-flex items-center gap-1.5 border-2 border-gold/70 bg-gold/15 px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-wider text-gold transition-colors ${captioning ? "opacity-70 cursor-wait" : "hover:bg-gold/25"}`}>
                  {captioning ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} strokeWidth={2.8} />} 🪄 ALIX_Generate_Tags
                </button>
              </div>
              <textarea data-testid="editor-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} rows={5}
                placeholder="Write a caption, or let ALIX generate one with tags…"
                className="w-full bg-black border-2 border-white/20 px-3 py-2.5 font-mono text-[11px] text-white/85 leading-relaxed resize-none focus:outline-none focus:border-gold/60 placeholder:text-white/25" />

              <div className="flex items-center justify-between gap-3 pt-1">
                <label className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-white/40">
                  VISIBILITY:
                  <select data-testid="editor-visibility-select" value={visibility} onChange={(e) => setVisibility(e.target.value)}
                    className="bg-black border-2 border-white/25 px-2 py-1 font-mono text-[10px] font-black uppercase tracking-wider text-gold focus:outline-none focus:border-gold/60">
                    <option value="public">PUBLIC</option>
                    <option value="private">PRIVATE</option>
                  </select>
                </label>
                <span className="font-mono text-[8px] uppercase tracking-widest text-white/25">+ ALIX credit tag</span>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button type="button" data-testid="editor-post-cancel" onClick={() => setPostOpen(false)}
                  className="inline-flex items-center gap-1 font-mono text-[10px] font-black uppercase tracking-widest text-white/50 hover:text-gold transition-colors">
                  <X size={12} strokeWidth={3} /> Cancel
                </button>
                <button type="button" onClick={publishPost} disabled={publishing} data-testid="editor-publish-btn"
                  className={`inline-flex items-center gap-2 border-2 border-gold bg-gold text-black px-4 py-2 font-mono text-[11px] font-black uppercase tracking-wider transition-colors ${publishing ? "opacity-70 cursor-wait" : "hover:bg-gold/80"}`}>
                  {publishing ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} strokeWidth={2.6} />} Publish
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
